import React, { useEffect, useState } from 'react';
import { Dot, TimeElapsed } from '@missionlabs/smartagent-app-components';
import DownArrow from '../../images/arrow-down.svg';
import classNames from 'classnames';
import './status.scss';
import { useDispatch, useSelector } from 'react-redux';
import useSettings from 'hooks/redux/useSettings';
import { getSettings } from 'store/settings/settings.actions';
import { setState } from 'store/user/user.actions';
import useHasFeature, { AppFeatures, useGetFeatureConfig } from 'hooks/useHasFeature'
import RootState from "store/state";
import useUser from 'hooks/redux/useUser';
import { isNilOrEmpty } from 'utils';

interface Props {
    isAvailable: boolean;
    statusName: string;
    inStateSince?: Date | string;
    disabled?: boolean;
}

const Status: React.FC<Props> = ({
    isAvailable,
    statusName,
    inStateSince,
    disabled,
}) => {
    const { settings } = useSettings();

    const dispatch = useDispatch();
    const hasFeature = useHasFeature();
    const featureConfig = useGetFeatureConfig();

    const { states } = useUser();
    const [isAwol, setAwol] = useState(false);

    const userState = useSelector<RootState, RootState['user']>(
        (state) => state.user
    );
    const { showNextStatus, afterCallStatus } = userState;

    // Handle the agent-status feature
    const agentStatusFeature = hasFeature(AppFeatures.AGENT_STATUS);
    const awolStatusName = featureConfig(AppFeatures.AGENT_STATUS)?.awolStatusName;

    const awolState = (
        states.find(state => state.name === awolStatusName) ||
        states.find(state => state.name === 'Offline')
    );

    useEffect(
        () => {
            if (agentStatusFeature && awolStatusName && isAwol && awolState) {
                dispatch(setState(awolState));
                setAwol(false);
            }
        },
        [isAwol, awolState, dispatch, agentStatusFeature, awolStatusName]
    )

    useEffect(() => {
        if (isNilOrEmpty(settings))
            dispatch(getSettings());
    }, [settings, dispatch]);

    const checkLimit = () => {
        if (!inStateSince) return null;

        const date =
            typeof inStateSince === 'object'
                ? inStateSince
                : new Date(inStateSince);
        const durationMilliSecs = new Date(date).getTime();
        const secondsSinceChange = Math.round(
            (new Date().getTime() - durationMilliSecs) / 1000,
        );
        const status = statusName;
        const limit =
            settings?.agentStatusTimes?.find(
                element =>
                    element.agentStatus.toLowerCase() === status.toLowerCase(),
            )?.timeLimitSecs || 0;
        return {
            duration: durationMilliSecs,
            awol: (limit > 0 && secondsSinceChange > limit) ? true : false,
        };
    };

    const renderTimer = () => {
        const limit = checkLimit();

        const duration = limit?.duration;
        if (!duration) return null;
        const awol = limit?.awol;
        const date = new Date(duration);
        const seconds = (new Date().getTime() - duration) / 1000;

        if (agentStatusFeature && awolStatusName && !isAwol && awol)
            setAwol(true);

        return (
            <span className={awol ? 'overTime' : 'underTime'}>
                <TimeElapsed
                    ariaLive="off"
                    annotate
                    date={date}
                    hours={seconds > 3600}
                    days={seconds > 3600 * 24}
                />
            </span>
        );
    };

    return (
        <div
            role="status"
            className={classNames({
                'sa-status': true,
                disabled,
            })}
        >
            <Dot type={isAvailable ? 'positive' : 'negative'} />
            <span className="sa-status-name">
                <span className="only-visible-to-screen-readers">Current status:</span>
                {statusName}
            </span>

            {!showNextStatus && renderTimer()}

            {showNextStatus &&
                <>
                    <div className='sa-status-verticalLine' />
                    <span className="sa-status-name">
                        Next:
                    </span>

                    <Dot type={afterCallStatus?.name === 'Available' ? 'positive' : 'negative'} />
                    <span className='sa-status-name'>
                        {afterCallStatus?.name}
                    </span>
                </>

            }

            {inStateSince && !disabled && (
                <img src={DownArrow} width="20" height="20" alt="" />
            )}
        </div>
    );
};

export default Status
